import type { RootState } from 'src/redux/store';

import { debounce } from 'es-toolkit';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import {
  Box,
  List,
  Avatar,
  Dialog,
  Divider,
  Tooltip,
  ListItem,
  TextField,
  Typography,
  IconButton,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  CircularProgress,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { useGetUserListMutation } from 'src/pages/setting/components/users/slice/userApi';

import { Iconify } from 'src/components/iconify';

interface User {
  id: string;
  name: string;
  number: string;
}

interface TransferUserProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  onTransfer?: (userId: string) => void;
}

export const TransferUser: React.FC<TransferUserProps> = ({
  open,
  anchorEl,
  handleClose,
  onTransfer,
}) => {
  const [getUserList, { isLoading, error }] = useGetUserListMutation();
  const [users, setUsers] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const { t } = useTranslate('translation');
  const { loginApiData } = useAppSelector((state) => (state as RootState).auth);
  const listRef = useRef<HTMLUListElement | null>(null);

  // 🟡 Common reusable fetch function
  const fetchUsers = async ({
    pageVal,
    search = '',
    reset = false,
  }: {
    pageVal: number;
    search?: string;
    reset?: boolean;
  }) => {
    try {
      const response: any = await getUserList({
        page: pageVal,
        limit: pageSize,
        filters: [{ fieldname: 'status', search: 'ACTIVE' }],
        globalSearch: search || undefined,
      }).unwrap();

      const newUsers = response?.list || [];
      setUsers((prev) => {
        if (reset) return newUsers;
        const existingIds = new Set(prev.map((user) => user.id));
        const filtered = newUsers.filter((user: any) => !existingIds.has(user.id));
        return [...prev, ...filtered];
      });

      setTotalPages(Math.ceil(response?.total / pageSize));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // 🟢 Reset on close
  useEffect(() => {
    if (!open) {
      setUsers([]);
      setContacts([]);
      setPage(1);
      setTotalPages(0);
      setSearchQuery('');
    }
  }, [open]);

  // 🟢 Fetch users on open or page change
  useEffect(() => {
    if (open) {
      fetchUsers({ pageVal: page, search: searchQuery });
    }
  }, [open, page]);

  const debounceSearch = useCallback(
    debounce((value: string) => {
      setPage(1); // reset page to 1 on new search
      fetchUsers({ pageVal: 1, search: value, reset: true });
    }, 500),
    []
  );
  // 🟢 Search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    debounceSearch(value);
  };

  // 🟢 Tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // 🟢 User selection
  const handleUserClick = (id: string) => {
    onTransfer?.(id);
    handleClose();
  };

  // 🟢 Filter logic
  const filteredUsers = users.filter((user) =>
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredContacts = contacts.filter((contact) =>
    `${contact.name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 🟢 Scroll handler
  const handleScroll = () => {
    if (
      listRef.current &&
      listRef.current.scrollHeight - listRef.current.scrollTop === listRef.current.clientHeight
    ) {
      if (page < totalPages) {
        setPage((prev) => prev + 1);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <Box sx={{ p: 2, minWidth: 400 }}>
        {/* Tabs */}
        <Typography variant="subtitle1" gutterBottom sx={{ my: 2 }}>
          {t('TRANSFER_CALL_TO')}
        </Typography>
        {/* <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', my: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            sx={{ maxWidth: 600 }} // Optional: Adjust max width as per your layout
          >
            <Tab label="Users" />
            <Tab label="Contacts" />
          </Tabs>
        </Box> */}

        {/* Search Field */}
        <TextField
          fullWidth
          label={t('SEARCH')}
          variant="outlined"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ mb: 2 }}
        />

        {isLoading && page === 1 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error" sx={{ p: 2 }}>
            Error fetching user list. Please try again.
          </Typography>
        ) : (
          <List
            dense
            ref={listRef}
            component="ul"
            onScroll={handleScroll}
            sx={{ maxHeight: '400px', overflowY: 'auto' }}
          >
            {selectedTab === 0 && loginApiData && filteredUsers.length > 0 ? (
              filteredUsers.map((user: any) => (
                <Box key={user.id}>
                  <ListItemButton
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      alignItems: 'center',
                      px: 2,
                    }}
                    disabled={
                      !user.extNumber || ('id' in loginApiData && loginApiData.id === user?.id)
                    } // Disable the item if `user.extNumber` is falsy
                  >
                    <ListItemAvatar>
                      <Avatar>
                        {user.firstName?.[0] || 'U'}
                        {user.lastName?.[0] || ''}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${user.firstName || 'Unknown'} ${user.lastName || ''}`}
                      secondary={`Ext: ${user.extNumber || ''}`}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{ color: 'text.secondary' }}
                    />
                    <Tooltip title={t('TRANSFER_CALL')}>
                      <IconButton onClick={() => handleUserClick(user.extNumber)}>
                        <Iconify icon="mingcute-transfer-line" />
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>

                  <Divider variant="inset" component="li" />
                </Box>
              ))
            ) : selectedTab === 1 && filteredContacts.length > 0 ? (
              filteredContacts.map((contact: any) => (
                <Box key={contact.id}>
                  <ListItem
                    onClick={() => handleUserClick(contact.number)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      alignItems: 'center',
                      px: 2,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar>{contact.name?.[0]}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={contact.name}
                      secondary={`Phone: ${contact.number}`}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{ color: 'text.secondary' }}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </Box>
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 2, py: 1, textAlign: 'center' }}
              >
                {t('NO_USER_FOUND')}
              </Typography>
            )}
          </List>
        )}
      </Box>
    </Dialog>
  );
};
